// src/App.tsx
import React from 'react';
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";

import SidePanel from "./components/SidePanel";
import TopHeader from "./components/TopHeader";
import Footer from "./components/Footer";

import Home from "./components/home/Home";
import About from "./components/About";
import Contact from "./components/Contact";
import TenantsModule from './components/admin/tenant/TenantIndex';
import UsersModule from './components/admin/user/UserIndex';
import ContractIndex from './components/contract/ContractIndex';
import SecurityDepositForm from './components/contract/security-deposit/SecurityDepositForm';

import ProtectedRoute from './routes/ProtectedRoutes';
import PublicRoute from './routes/PublicRoutes';
import Login from './components/auth/Login';

import { AuthProvider, useAuth } from './context/AuthContext';
import { Module } from 'module';
import ModuleIndex from './components/admin/modules/ModuleIndex';
import RoleIndex from './components/admin/roles/RoleIndex';
import RoleModulePermissionIndex from './components/admin/roles/rolemoduleperssion/RoleModulePermissionIndex';
import UserRoleMappingIndex from './components/admin/userrolemapping/UserRoleMappingIndex';
import UnderConstructionPage from './components/UnderConstructionPage';
import ProjectsModule from './components/project/ProjectIndex';
import TasksModule from './components/project/task/TaskIndex';
import Unauthorized from './Unauthorized';
import WebSocketTestPage from './components/chat/WebsocketTestPage';
import DocumentList from './components/documents/DocumentList';
import DocumentForm from './components/documents/DocumentForm';
import DocumentIndex from './components/documents/DocumentIndex';
import MultipleFileUploadPage from './components/documents/MultipleFileUploadPage';
import { TaskListV2 } from './components/project/task/TaskListV2';
import TaskCommentsModule from './components/taskComments/TaskCommentIndex';
import FileUploader from './components/common/FileUploader';
import { Upload } from 'lucide-react';
import UploadPage from './components/common/UploadPage';
import { SelectedProjectTaskList } from './components/project/SelectedProjectTaskList';
import CommentsPage from './components/comments/CommentsPage';
import MessagingUI from './components/comments/MessagingUI';
import ProjectDashboard from './components/project/ProjectDashboard';
import { ProjectList } from './components/project/ProjectList';
import RemindersModule from './components/reminders/ReminderIndex';
import GeneralTaskModule from './components/general-task/GeneralTaskIndex';
import TaskAssignmentIndex from './components/task-assignment/TaskAssignmentIndex';
import WebSocketTester from './components/websocket/WebSocketTester';
import WebSocketTradeTable from './components/websocket/WebSocketTradeTable';
import UserHomeIndex from './components/user-home/UserHomeIndex';
import DriverDashboard from './components/vms/driver/DriverDashboard';
import DriverModule from './components/vms/driver/DriverIndex';

// ✅ Layout wrapper that uses auth context internally
const AppLayout: React.FC = () => {
  const { isAuthenticated } = useAuth();

  return (
    <>
      <TopHeader />
      <div className="flex h-screen overflow-y-auto">
        {isAuthenticated && <SidePanel />}
        <div className="flex-grow bg-gray-100 overflow-y-auto">
          <Routes>
            {/* ✅ Public Routes */}
            <Route path="/" element={<PublicRoute element={<Home />} />} />
            <Route path="/about" element={<PublicRoute element={<About />} />} />
            <Route path="/contact" element={<PublicRoute element={<Contact />} />} />
            <Route path="/unauthorized" element={<PublicRoute element={<Unauthorized />} />} />
            <Route path="/login" element={<Login />} />

            {/* 🔒 Protected Routes */}
            <Route path="/admin/tenants/*" element={<ProtectedRoute element={<TenantsModule />} />} />
            <Route path="/admin/users/*" element={<ProtectedRoute element={<UsersModule />} />} />
            <Route path="/admin/modules/*" element={<ProtectedRoute element={<ModuleIndex />} />} />
            <Route path="/admin/roles/*" element={<ProtectedRoute element={<RoleIndex/>} />} />
            {/* <Route path="/admin/role-module-permissions/*" element={<ProtectedRoute element={<RoleModulePermissionIndex />} />} /> */}
            <Route path="/admin/user-roles-mapping/*" element={<ProtectedRoute element={<UserRoleMappingIndex />} />} />
            <Route path="/admin/user-module-permissions/*" element={<ProtectedRoute element={<UsersModule />} />} />

            <Route path="/contracts/*" element={<ProtectedRoute element={<ContractIndex />} />} />
            <Route path="/contracts/:contractId/security-deposit/create" element={<ProtectedRoute element={<SecurityDepositForm />} />} />
            <Route path="/contracts/:contractId/security-deposit/:depositId/edit" element={<ProtectedRoute element={<SecurityDepositForm />} />} />
            <Route path="/contracts/:contractId/security-deposit/:depositId/view" element={<ProtectedRoute element={<SecurityDepositForm />} />} />

            {/* Dummy protected placeholders */}
            {/* <Route path="/contracts-deposits/*" element={<ProtectedRoute element={<UsersModule />} />} /> */}
            <Route path="/uploads" element={<ProtectedRoute element={<UploadPage />} />} />
            <Route path="/projects/*" element={<ProtectedRoute element={<ProjectsModule />} />} />
            <Route path="/projects/list" element={<ProtectedRoute element={<ProjectList />} />} />
            
            <Route path="/project-tasks/*" element={<ProtectedRoute element={<TasksModule />} />} />
            <Route path="/selected-project-tasks/*" element={<ProtectedRoute element={<SelectedProjectTaskList />} />} />
            <Route path="/project-tasks/:taskId/comments/*" element={<ProtectedRoute element={<TaskCommentsModule />} />} />
            <Route path="/inventories/*" element={<ProtectedRoute element={<UnderConstructionPage />} />} />
            <Route path="/payroll/*" element={<ProtectedRoute element={<UnderConstructionPage />} />} />
            <Route path="/attendance/*" element={<ProtectedRoute element={<UnderConstructionPage />} />} />
            <Route path="/tasks/*" element={<ProtectedRoute element={<UnderConstructionPage />} />} />
            <Route path="/general-tasks/*" element={<ProtectedRoute element={<GeneralTaskModule />} />} />
            <Route path="/task-assignments/*" element={<ProtectedRoute element={<TaskAssignmentIndex />} />} />

            <Route path="/user-home/*" element={<ProtectedRoute element={<UserHomeIndex />} />} />
            <Route path="/profile/*" element={<ProtectedRoute element={<UnderConstructionPage />} />} />
            <Route path="/vms/*" element={<ProtectedRoute element={<DriverModule />} />} />
            <Route path="/vms/driver/*" element={<ProtectedRoute element={<DriverModule />} />} />
            
            <Route path="/vehicle-insurance/*" element={<ProtectedRoute element={<UnderConstructionPage />} />} />
            <Route path="/payments/*" element={<ProtectedRoute element={<UnderConstructionPage />} />} />
            <Route path="/estimations/*" element={<ProtectedRoute element={<UnderConstructionPage />} />} />
            <Route path="/reports/*" element={<ProtectedRoute element={<UnderConstructionPage />} />} />
            <Route path="/notifications/*" element={<ProtectedRoute element={<UnderConstructionPage />} />} />
            <Route path="/user-profile/*" element={<ProtectedRoute element={<UnderConstructionPage />} />} />
            <Route path="/gst/*" element={<ProtectedRoute element={<UnderConstructionPage />} />} />
            <Route path="/reminders/*" element={<ProtectedRoute element={<RemindersModule />} />} />
            <Route path="/comments/*" element={<ProtectedRoute element={<CommentsPage />} />} />
            <Route path="/barqlytics" element={<ProtectedRoute element={<WebSocketTradeTable />} />} />
             
          </Routes>
        </div>
      </div>
      <Footer />
    </>
  );
};

// ✅ AuthProvider wraps the entire app context
const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <AppLayout />
      </Router>
    </AuthProvider>
  );
};

export default App;